import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const DashboardLayout = lazy(() => import("@/components/DashboardLayout"));
const DashboardOverview = lazy(() => import("@/pages/DashboardOverview"));
const TasksPage = lazy(() => import("@/pages/TasksPage"));
const MilestonesPage = lazy(() => import("@/pages/MilestonesPage"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
const FinanceDashboardPage = lazy(() => import("@/pages/FinanceDashboardPage"));
const InvestorPortalPage = lazy(() => import("@/pages/InvestorPortalPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const AIInsightsPage = lazy(() => import("@/pages/AIInsightsPage"));
const PitchGeneratorPage = lazy(() => import("@/pages/PitchGeneratorPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));

function ProtectedRoute({ children }) {
  const { user, loading, startups, startupsLoaded } = useAuth();
  if (loading || !startupsLoaded) return <div className="flex h-screen items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/auth" />;
  if (startups.length === 0 && window.location.pathname !== '/onboarding') return <Navigate to="/onboarding" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading, startups, startupsLoaded } = useAuth();
  if (loading || !startupsLoaded) return <div className="flex h-screen items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (user && startups.length > 0) return <Navigate to="/dashboard" />;
  if (user && startups.length === 0) return <Navigate to="/onboarding" />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardOverview />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="milestones" element={<MilestonesPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="finance" element={<FinanceDashboardPage />} />
        <Route path="investors" element={<InvestorPortalPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="ai-insights" element={<AIInsightsPage />} />
        <Route path="pitch" element={<PitchGeneratorPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
